// ─── Belege auf Faktebene ────────────────────────────────────────────────────
//
// Eine Quelle an der Person sagt nur, DASS es einen Beleg gibt. Für seriöse
// Genealogie zählt aber, WOFÜR: „dieses Geburtsdatum stammt aus jenem
// Kirchenbuch, Seite 3." Dazu kommt der Sicherheitsgrad – ohne ihn ist ein
// belegtes Datum optisch nicht von einem geratenen „ca. 1850" zu unterscheiden.

/** Angaben einer Person, die einzeln belegt werden können. */
export const FACT_KEYS = [
  'name',
  'birthDate',
  'birthPlace',
  'deathDate',
  'deathPlace',
  'occupation',
  'parents',
  'marriage',
] as const;

export type FactKey = (typeof FACT_KEYS)[number];

export const FACT_LABELS: Record<FactKey, string> = {
  name: 'Name',
  birthDate: 'Geburtsdatum',
  birthPlace: 'Geburtsort',
  deathDate: 'Sterbedatum',
  deathPlace: 'Sterbeort',
  occupation: 'Beruf',
  parents: 'Eltern',
  marriage: 'Heirat',
};

/**
 * Sicherheitsgrade, absteigend nach Belastbarkeit. Die Reihenfolge ist
 * bedeutsam: bei mehreren Belegen für dieselbe Angabe gewinnt der beste –
 * außer es liegt ein Widerspruch vor, der immer sichtbar bleiben muss.
 */
export const CONFIDENCE_LEVELS = ['primary', 'secondary', 'assumed', 'conflicting'] as const;

export type ConfidenceLevel = (typeof CONFIDENCE_LEVELS)[number];

export const CONFIDENCE_LABELS: Record<ConfidenceLevel, string> = {
  primary: 'Gesichert',
  secondary: 'Wahrscheinlich',
  assumed: 'Vermutet',
  conflicting: 'Widersprüchlich',
};

export const CONFIDENCE_DESCRIPTIONS: Record<ConfidenceLevel, string> = {
  primary: 'Zeitnahe Primärquelle, z. B. Kirchenbuch- oder Standesamtseintrag.',
  secondary: 'Sekundärquelle oder Abschrift, z. B. Ortsfamilienbuch, Todesanzeige.',
  assumed: 'Erschlossen oder überliefert, ohne Dokument – z. B. aus dem Heiratsalter.',
  conflicting: 'Quellen widersprechen sich – muss noch geklärt werden.',
};

/** Rangfolge für „bester Beleg gewinnt". Kleiner = belastbarer. */
const CONFIDENCE_RANK: Record<ConfidenceLevel, number> = {
  primary: 0,
  secondary: 1,
  assumed: 2,
  conflicting: 3,
};

export function isConfidenceLevel(value: string): value is ConfidenceLevel {
  return (CONFIDENCE_LEVELS as readonly string[]).includes(value);
}

export function isFactKey(value: string): value is FactKey {
  return (FACT_KEYS as readonly string[]).includes(value);
}

/**
 * Fasst mehrere Belege einer Angabe zu einem angezeigten Sicherheitsgrad
 * zusammen. Ein Widerspruch schlägt alles andere – sonst zählt der beste Beleg.
 */
export function summarizeConfidence(
  levels: readonly ConfidenceLevel[],
): ConfidenceLevel | null {
  if (levels.length === 0) return null;
  if (levels.includes('conflicting')) return 'conflicting';
  return levels.reduce((best, cur) =>
    CONFIDENCE_RANK[cur] < CONFIDENCE_RANK[best] ? cur : best,
  );
}
